/* eslint-disable prettier/prettier */
export const APP_TITLE = 'FinSuite';

export const DEFAULT_LOCALE_EN_US = 'en-US';
export const TRANSLATIONS_DIR_NAMESPACE = 'translations';
export const TRANSLATION_ROUTE = `/${TRANSLATIONS_DIR_NAMESPACE}`;

export const eLDAPGroup = {
  DEV_LDAP: 'das-finsuite-application-dev',
  ADMIN_LDAP: 'das-finsuite-application-admins',
  OE_RO_LDAP: 'das-finsuite-application-oe-ro',
  // https://permissions.amazon.com/a/team/das-finsuite-baseball-cards
  BASEBALL_CARDS_LDAP: 'das-finsuite-baseball-cards',
  // https://permissions.amazon.com/a/team/das-finsuite-huddleplus-admin
  HUDDLEPLUS_ADMIN_LDAP: 'das-finsuite-huddleplus-admin',
  // https://permissions.amazon.com/a/team/das-fintech-platform
  FINTECH_PLATFORM_LDAP: 'das-fintech-platform',
  KINGPIN_DIGITAL_READ_GROUP: 'kp-digital-read',
  KINGPIN_DIGITAL_EDIT_GROUP: 'kp-digital-edit',
  KINGPIN_DIGITAL_ADMIN_GROUP: 'kp-digital-admin',
  KINGPIN_DAS_FINANCE_ADMIN_GROUP: 'caruccio-ipat-all',
  // https://permissions.amazon.com/a/team/DaS-TM1-Users
  DAS_TM1_USERS_GROUP: 'das-tm1-users',
  // Canary (Connections scorecard) access tiers
  // https://permissions.amazon.com/a/team/das-canary-scorecard-powerusers
  CANARY_SCORECARD_POWERUSERS_LDAP: 'das-canary-scorecard-powerusers',
  // https://permissions.amazon.com/a/team/das-canary-scorecard-viewers
  CANARY_SCORECARD_VIEWERS_LDAP: 'das-canary-scorecard-viewers'
};

export const KINGPIN_ACCESS_GROUPS: string[] = [
  eLDAPGroup.DEV_LDAP,
  eLDAPGroup.KINGPIN_DAS_FINANCE_ADMIN_GROUP,
  eLDAPGroup.KINGPIN_DIGITAL_READ_GROUP,
  eLDAPGroup.KINGPIN_DIGITAL_EDIT_GROUP,
  eLDAPGroup.KINGPIN_DIGITAL_ADMIN_GROUP,
  eLDAPGroup.DAS_TM1_USERS_GROUP
];

export const ADMIN_LDAP_GROUPS: string[] = [eLDAPGroup.DEV_LDAP, eLDAPGroup.ADMIN_LDAP];
export const DEV_LDAP_GROUPS: string[] = [eLDAPGroup.DEV_LDAP];
export const VPC_CIDR_EDIT_LDAP_GROUPS: string[] = [eLDAPGroup.DEV_LDAP, eLDAPGroup.FINTECH_PLATFORM_LDAP];
// Members of these groups can access the Huddle+ admin page (gear icon + /admin/ea-huddle).
export const HUDDLEPLUS_ADMIN_LDAP_GROUPS: string[] = [eLDAPGroup.HUDDLEPLUS_ADMIN_LDAP, eLDAPGroup.DEV_LDAP];

// AWS RUM default casing convention.
// convention: element_action_name
export enum eRUM_CUSTOM_EVENT {
  USER_LOGGED_IN = 'user_logged_in'
}

export const FINSUITE_DOMAINS = ['fintech.device.finance.amazon.dev', 'finsuite.device.finance.amazon.dev'];

export const USER_BADGE_PHOTO_BASE_URL = 'https://badgephotos.corp.amazon.com/?uid=';
export const USER_BADGE_PHOTO_FULLSIZE_URL = 'https://badgephotos.corp.amazon.com/?Region=master&FullsizeImage=Yes&uid=';
export const QUICKSIGHT_HOME_URL = 'https://us-east-1.quicksight.aws.amazon.com/sn/account/amazonbi/start/home';

export const PHONE_TOOL_URL = 'https://atoz.amazon.work/phonetool/users/';
export const TICKETS_TOOL_URL = 'https://t.corp.amazon.com/';
export const ATOZ_PROFILE_URL = 'https://atoz.amazon.work/profile';
export const SLACK_ICON_URL = 'https://a.slack-edge.com/80588/marketing/img/icons/icon_slack_hash_colored.png';
export const SLACK_DM_URL = 'https://my.slack.com/app_redirect?channel=@';
export const OFFICE_HOURS_WIKI_URL = 'https://w.amazon.com/bin/view/FDA_%26_AD3_Team_Office_Hours_-_XWiki/';

export const generateProfilePictureUrl = (loginName: string): string => {
  return `${USER_BADGE_PHOTO_BASE_URL}${loginName}`;
};

export const generatePhoneToolUrl = (loginName: string): string => {
  return `${PHONE_TOOL_URL}${loginName}`;
};

export const TICKETS_FOR_OE_ERROR_URL = 'https://t.corp.amazon.com/create/templates/161712b7-f28d-41b3-b6a2-f4110867978d';

export enum eTicketLinks {
  DAS_FINSUITE_GENERAL_ISSUE = 'https://t.corp.amazon.com/create/templates/b74ae449-480e-4ce3-9ddc-d89af3cf5dfe',
  DAS_FINSUITE_SUMMARIZER_ISSUE = 'https://t.corp.amazon.com/create/templates/2d0edec5-6161-4e70-9f6b-7822f2545c2d'
}

export enum eCacheKeys {
  UserAuthDetails = '-userAuthDetails-v2'
}

export enum eLocalStorageKeys {
  IsAdminControlsEnabled = 'isAdminControlsEnabled-v2',
  IsDarkModeEnabled = 'isDarkModeEnabled-v2',
  DFP_HOME_PAGE_WIDGETS_LAYOUT = 'dfp-home-page-widgets-layout-v1',
  APP_SPECIFIC_PAGE_WIDGETS_LAYOUT = 'app-specific-widgets-layout',
  FINTECH_TEAM_PAGE_WIDGETS_LAYOUT = 'fintech-team-page-widgets-layout',
  AskQuickPanelWidth = 'askQuickPanelWidth',
  AskQuickPanelOpen = 'askQuickPanelOpen',
  SelectedAssistant = 'selectedAssistant',
  UserPreferences = 'finsuite_user_preferences',
  Locale = 'finsuite-locale'
}

export enum eUserPreferenceKeys {
  // All Apps
  ALL_APPS_TABLE_WIDTHS = 'All-Apps-Table-Widths-v2',
  ALL_APPS_TABLE_PREFERENCES = 'All-Apps-Table-Preferences-v5',
  ALL_APPS_VIEW_MODE = 'All-Apps-View-Mode',
  // ALL_APPS_TABLE_FILTERING_PROPERTIES = "All-Apps-Table-Filtering-Properties",

  // General User Announcements
  ANNOUNCEMENTS_TABLE_WIDTHS = 'Announcements-Table-Widths',
  ANNOUNCEMENTS_TABLE_PREFERENCES = 'Announcements-Table-Preferences',
  // ANNOUNCEMENTS_TABLE_FILTERING_PROPERTIES = "Announcements-Filtering-Properties",

  // Admin - Applications
  ADMIN_APPLICATIONS_TABLE_WIDTHS = 'Admin-Applications-Table-Widths-1',
  ADMIN_APPLICATIONS_TABLE_PREFERENCES = 'Admin-Applications-Table-Preferences-v2',
  // ADMIN_APPLICATIONS_TABLE_FILTERING_PROPERTIES = "Admin-Applications-Table-Filtering-Properties",

  // Admin - Announcements
  ADMIN_ANNOUNCEMENTS_TABLE_WIDTHS = 'Admin-Announcements-Table-Widths',
  ADMIN_ANNOUNCEMENTS_TABLE_PREFERENCES = 'Admin-Announcements-Table-Preferences',
  // ADMIN_ANNOUNCEMENTS_TABLE_FILTERING_PROPERTIES = "Admin-Announcements-Table-Filtering-Properties",

  // Admin - Feedback
  ADMIN_FEEDBACK_TABLE_WIDTHS = 'Admin-Feedback-Table-Widths',
  ADMIN_FEEDBACK_TABLE_PREFERENCES = 'Admin-Feedback-Table-Preferences'
  // ADMIN_FEEDBACK_TABLE_FILTERING_PROPERTIES = "Admin-Feedback-Table-Filtering-Properties"
}

// TODO - Yet to implement global flashbar notifications
export enum eFinSuiteMessages {
  NO_INFORMATION_WIDGETS = 'This app page is work-in-progress.  Please check back for updates!',
  NO_INFORMATION_INFO_PANEL = 'This app’s Help/Support panel is work-in-progress. Please check back for updates!',
  ADMIN_CONTROLS_ENABLED = 'Additional functionality for admins will now appear to the right of the page header when available. Any changes made using these controls will be seen by all users.',
  ADMIN_CONTROLS_DISABLED = 'Additional functionality for admins will now be hidden from the user interface.',
  DARK_MODE_ENABLED = 'To switch to Light mode, click on the moon icon and select Light mode.',
  DARK_MODE_DISABLED = 'To switch to Dark mode, click on the moon icon and select Dark mode.'
}

export enum eFinSuiteAdminMessages {
  REQUIRED_FIELD = 'Required field',
  INVALID_ALIAS = 'Invalid alias',
  VALID_URL = 'Invalid URL',
  ENTER_VALID_NUMBER = 'Enter a valid number',
  UNIQUE_POSITION_ERROR = 'Position must be unique',
  AT_LEAST_ONE_SELECTION = 'Select at least one option',
  AT_LEAST_ONE_RECORD = 'If this feature is enabled, add at least one record',
  UNIQUE_APP_NAME_ERROR = 'Application name already exists',
  NEXT_BUTTON_VALIDATION_ERROR = 'Complete all the required fields to continue',
  LINK_VALIDATION_ERROR = 'Links must be valid https URLs.',
  URL_PATH_VALIDATION_ERROR = 'App-specific Path is invalid. Use only lowercase letters, numbers, dashes, and underscores — see the App-specific Path field for details.',

  APP_STATUS_ACTIVE_HEADER = ' is now Active',
  APP_STATUS_ACTIVE_MESSAGE = 'Active applications will have their homepage and navigation references restored.',

  APP_STATUS_INACTIVE_HEADER = ' is now Inactive',
  APP_STATUS_INACTIVE_MESSAGE = 'Inactive applications will have their homepage archived and navigation references removed.'
}

export enum eFinSuiteAnnouncementMessages {
  ANNOUNCEMENT_SUCCESSFULLY_ADDED = 'Announcement has been added successfully.',
  ANNOUNCEMENT_CREATION_FAILED = 'An error occurred while submitting your new announcement. Try again or submit a ticket.',

  ANNOUNCEMENT_SUCCESSFULLY_UPDATED = 'Announcement has been updated successfully.',
  ANNOUNCEMENT_UPDATE_FAILED = 'An error occurred while submitting your changes to announcement. Try again or submit a ticket.',

  ANNOUNCEMENT_SUCCESSFULLY_DELETED = 'Announcement has been deleted successfully.',
  ANNOUNCEMENT_DELETION_FAILED = 'An error occurred while deleting announcement. Try again or submit a ticket.'
}

export enum eFinSuiteFeedbackMessages {
  FEEDBACK_SUCCESSFULLY_ADDED = 'Your feedback has successfully been submitted.',
  FEEDBACK_CREATION_FAILED = 'There was a problem submitting your feedback. Try again.',

  FEEDBACK_SUCCESSFULLY_DELETED = 'Feedback has been deleted successfully.',
  FEEDBACK_DELETION_FAILED = 'An error occurred while deleting feedback. Try again or submit a ticket.'
}

export enum appSyncOperationName {
  QUERY = 'query',
  MUTATION = 'mutation'
}

export enum ProfileDropDownPanelLinks {
  // MY STUFF
  PROFILE = 'https://atoz.amazon.work/profile',
  ATOZ = 'https://atoz.amazon.work/',
  TIME_OFF = 'https://atoz.amazon.work/time',
  BADGE_REPORT = 'https://atoz.amazon.work/myhr/livehelp/landing?category=My%20Employment&type=In%20Office%20Expectations&item=Badge%20Report&preferredChannel=SSA',
  IVY = 'https://ivy.talent.a2z.com/',
  TASKS = 'https://ivy.talent.a2z.com/tasks',

  // WORKPLACE
  PHONE_TOOL = 'https://atoz.amazon.work/phonetool/users/',
  BOOK_ROOM = 'https://meetings.amazon.com/#/room-booking',
  WIKI = 'https://w.amazon.com/bin/view/Main/',
  SHOUT_OUTS = 'https://atoz.amazon.work/shout-outs',
  PASSWORD_UNLOCK = 'https://password-v2.corp.amazon.com/',

  // MANAGER HUB
  CONNECTIONS = 'https://connections-reporting.corp.amazon.com/',
  CLARITY = 'https://clarity.people-insight.a2z.com/',
  ROSTER = 'https://roster2.a2z.com/',
  BLUEPRINT = 'https://atoz.amazon.work/blueprint/scenario',
  HIRE = 'https://hire.amazon.com/',
  TEAMS = 'https://permissions.amazon.com/a/team/',
  EMAIL_LISTS = 'https://email-list.corp.amazon.com/email-list/',

  // ACCESS
  AWS_ACCOUNT_ACCESS = 'https://iad.merlon.amazon.dev/accounts?filter=allAccounts#accounts',
  TROUBLE_TICKETING = 'https://t.corp.amazon.com/',
  PERMISSION_GROUPS = 'https://permissions.amazon.com/',
  IT_SUPPORT = 'https://my.it.a2z.com/',
  IT_MARKETPLACE = 'https://shop.it.a2z.com/',

  // TRAVEL
  BOOK_TRAVEL = 'https://app.spotnana.com/',
  TRAIL = 'https://trail.amazon.dev/',
  TRAVEL_PROMOTIONS = 'https://w.amazon.com/bin/view/TravelAndEvents/BusinessTravelPromotions/',
  SUBMIT_EXPENSES = 'https://expenses.corp.amazon.com/',
  MOVE_IN_SYNC = 'https://amz4.moveinsync.com/RAC/',

  // GROWTH
  LEARNING_HUB = 'https://atoz.amazon.work/learn/home',
  JOB_FINDER = 'https://atoz.amazon.work/jobs/',
  OTHER_HELPFUL_LINKS = 'https://w.amazon.com/bin/view/Main/AmazonLinks'
}

export const MODULE_DISPLAY_ORDER: readonly string[] = [
  'Units & Revenue',
  'Promo Spend',
  'Gross Profit',
  'COGS',
  'OpEx',
  'Operating Income',
  'Install Base Value (IBV)',
  'Lifetime Value',
  'Other'
];

export const ASK_QUICK_CHAT_URL = {
  default:
    'https://us-east-1.quicksight.aws.amazon.com/sn/account/amazonbi/embed/share/accounts/764946308314/chatagents/f7468fac-75e7-48a1-8c2c-6684230138c8',
  prophecy:
    'https://us-east-1.quicksight.aws.amazon.com/sn/account/amazonbi/embed/share/accounts/764946308314/chatagents/d39b0f4f-a784-41b7-a656-e079798b7656'
};
